import { Tag } from "@kamae-apps/shared/Components/Atoms/Tag/Tag"
import Icon from "@kamae-apps/shared/Components/Fondations/Icons/Icon"
import clsx from "clsx"
import type { TFunction } from "i18next"
import { FC } from "react"
import { SidebarMode } from "../../../Contexts/SidebarMode/SidebarModeContext"
import { StarterModal } from "../../Molecules/StarterModal/StarterAdModal"
import { WelcomeModal } from "../../WelcomeModal/WelcomeModal"
import { MenuEntryList } from "./MenuEntryList"
import { UserMenu } from "./UserMenu"
import { useMenu } from "./useMenu"

interface MenuProps {
  t: TFunction
  isNewsBarActive: boolean
}

export const Menu: FC<MenuProps> = ({ t, isNewsBarActive }: MenuProps) => {
  const {
    user,
    mode,
    isAdmin,
    isTeamLeader,
    userMenuEntries,
    displayWelcomeModal,
    setDisplayWelcomeModal,
    getAvatarUrl,
    getRoundedAvatarUrl,
    isStarterCompany,
    starterModal,
    setStarterModal,
    isPageScrolling,
  } = useMenu(t)

  const hasNewsBar = isNewsBarActive && mode !== SidebarMode.MOBILE
  const newsBarOffset = hasNewsBar ? (isPageScrolling ? "mt-0" : "mt-14") : ""

  return (
    <>
      <div
        data-testid={"sidebar"}
        className={clsx(
          "flex items-center z-20 fixed transition-all duration-300 ease-in-out",
          newsBarOffset,
          mode === SidebarMode.MOBILE && "order-1 w-full flex-row bg-neutral-white bottom-0 py-4 px-4 shadow-xs",
          mode === SidebarMode.COLLAPSED && "top-0 bottom-0 gap-4 bg-transparent flex-col pt-5 pb-3 px-4 w-auto",
          mode === SidebarMode.EXPANDED && "top-0 bottom-0 gap-4 bg-transparent flex-col p-5 w-60"
        )}
      >
        {mode === SidebarMode.COLLAPSED && (
          <Icon
            name={"kamaeBelt"}
            size={"m"}
          />
        )}
        {mode === SidebarMode.EXPANDED && (
          <img
            className={"h-[34px] w-full"}
            src={"/logo/kamae-logo.svg"}
            alt={"kamae-logo"}
          />
        )}
        {mode === SidebarMode.EXPANDED && isStarterCompany && isAdmin && (
          <button
            className={"border-secondary-soft-purple-300 border-1 rounded-lg px-1"}
            type={"button"}
            onClick={() => setStarterModal(true)}
          >
            <Tag variant={"tag-default-soft-purple"}>{t("starterLicence:licenceName")}</Tag>
          </button>
        )}
        {mode === SidebarMode.EXPANDED && (
          <div className={"relative w-[172px] h-[176px]"}>
            <img
              className={"absolute bottom-0 left-0 right-0 h-[140px] w-[172px]"}
              src={"/avatar/avatar-background.png"}
              alt={"avatar-background"}
            />
            <img
              className={"absolute bottom-0 left-0 right-0 mx-auto h-[176px] w-auto"}
              src={getAvatarUrl(user.avatar)}
              alt={"kamae-san"}
            />
          </div>
        )}
        <div
          className={clsx(
            "flex grow justify-between",
            mode === SidebarMode.MOBILE && "items-center",
            mode === SidebarMode.COLLAPSED && "flex-col w-full items-center",
            mode === SidebarMode.EXPANDED && "flex-col w-full"
          )}
        >
          <MenuEntryList
            t={t}
            isAdmin={isAdmin}
            isTeamLeader={isTeamLeader}
          />
          <div className={clsx("mt-auto", mode === SidebarMode.EXPANDED && "w-full")}>
            <UserMenu
              user={user}
              entries={userMenuEntries}
              getRoundedAvatarUrl={getRoundedAvatarUrl}
            />
          </div>
        </div>
      </div>
      {starterModal && (
        <StarterModal
          isDisplayed={starterModal}
          close={() => setStarterModal(false)}
        />
      )}
      {displayWelcomeModal && <WelcomeModal close={() => setDisplayWelcomeModal(false)} />}
    </>
  )
}
